import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  Pressable,
  Platform,
  LayoutChangeEvent,
} from 'react-native';
import { useSelector } from 'react-redux';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootState } from '../../redux/store';
import { toIsoDate, addDays, parseIsoDate } from '../../utils/localDate';
import { useToday } from '../../hooks/useToday';
import {
  selectMealsForDateRange,
  selectPlannedMealsForRecipe,
  PlannedMeal,
  MealType,
  getCurrentMondayStart,
  getWeekStartForDate,
  MAX_MEALS_PER_SLOT,
} from '../../redux/mealPlanSlice';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';
import { resolveSlotTap, upcomingEntriesForRecipe, asksIfCooked, slotDishLabel, formatDishList, slotRowCopy, type SlotActionOption, type SlotAction } from './planSheetLogic';
import type { MoveAction } from '../../services/mealMovePlan';

/** Which shape the sheet opens in. */
export type AddToMealPlanMode = 'add' | 'move';

/**
 * What a slot tap resolved to. `'move'` carries one of *this* recipe's own
 * meals to a new slot; `'replace'` empties the target slot — every dish in it,
 * not just the first (#845) — and puts this recipe there. Both used to be
 * called "move", which made the caller treat an eviction as if it were a
 * reschedule.
 */
export type AddToMealPlanAction = 'add' | 'move' | 'replace';

/**
 * What the cook said along with the tap. `cooked` is set only when they
 * answered « Oui, je l'ai cuisinée » to a day that is over (#846); the host
 * passes it on to `addToWeek` (or marks the moved meal), and nothing else
 * makes a meal cooked.
 */
export interface AddToMealPlanOptions {
  cooked?: boolean;
}

interface AddToMealPlanModalProps {
  visible: boolean;
  recipeId?: string;
  recipeTitle: string;
  onClose: () => void;
  onAdd: (
    date: string,
    mealType: MealType,
    action?: AddToMealPlanAction,
    fromMealId?: string,
    options?: AddToMealPlanOptions,
  ) => void;
  preselectedDate?: string;
  preselectedMealType?: MealType;
  /**
   * `'move'` opens the sheet against an existing planned meal: its slot is
   * marked "this meal is here" and every other slot is a one-tap move. The
   * default `'add'` keeps the original place-a-new-meal behaviour.
   */
  mode?: AddToMealPlanMode;
  /** The planned meal being moved. Required when `mode === 'move'`. */
  movingMealId?: string;
  /**
   * Called when the user picks a destination slot in move mode. `action` is
   * what they chose for an occupied slot — the sheet never decides for them.
   */
  onMove?: (mealId: string, date: string, mealType: MealType, action: MoveAction) => void;
  /** Called by "remove from plan" in move mode. */
  onRemove?: (mealId: string) => void;
}

interface DaySlots {
  date: string;
  dayName: string;
  dayIndex: number;
  isToday: boolean;
  isPast: boolean;
  slots: {
    mealType: MealType;
    meals: PlannedMeal[];
  }[];
}

const MEAL_TYPES: MealType[] = ['breakfast', 'lunch', 'dinner'];

/**
 * Button copy per choice, and why a choice is unavailable. Replace empties the
 * whole slot, so its label counts what goes (« Remplacer les 2 »).
 */
const SLOT_ACTION_LABEL_KEYS: Record<SlotAction, string> = {
  swap: 'recipeDetail.swapHere',
  replace: 'recipeDetail.replaceSlot',
  stack: 'recipeDetail.addToSlot',
};
const SLOT_BLOCKER_KEYS: Record<'slotFull' | 'entryOwnsSlot', string> = {
  slotFull: 'recipeDetail.slotFullReason',
  entryOwnsSlot: 'recipeDetail.entryOwnsSlotReason',
};
/** Stable empty reference so the selector does not churn when there is no id. */
const EMPTY_MEALS: PlannedMeal[] = [];
const DAY_NAME_KEYS = ['thisWeek.dayNames.monday', 'thisWeek.dayNames.tuesday', 'thisWeek.dayNames.wednesday', 'thisWeek.dayNames.thursday', 'thisWeek.dayNames.friday', 'thisWeek.dayNames.saturday', 'thisWeek.dayNames.sunday'];
const DAY_SHORT_KEYS = ['thisWeek.dayShort.mon', 'thisWeek.dayShort.tue', 'thisWeek.dayShort.wed', 'thisWeek.dayShort.thu', 'thisWeek.dayShort.fri', 'thisWeek.dayShort.sat', 'thisWeek.dayShort.sun'];

// All meal types are always available for planning today
// Users should be able to plan ahead even if the meal time has passed
const getAvailableMealsForToday = (): MealType[] => {
  return ['breakfast', 'lunch', 'dinner'];
};

export const AddToMealPlanModal: React.FC<AddToMealPlanModalProps> = ({
  visible,
  recipeId,
  recipeTitle,
  onClose,
  onAdd,
  preselectedDate,
  preselectedMealType,
  mode = 'add',
  movingMealId,
  onMove,
  onRemove,
}) => {
  const insets = useSafeAreaInsets();
  const { t, i18n } = useTranslation();
  const scrollViewRef = useRef<ScrollView>(null);
  const dayYPositions = useRef<Record<string, number>>({});
  const [modalWeekStart, setModalWeekStart] = useState(() => getCurrentMondayStart());

  // Every planned occurrence of this recipe, across all weeks — not just the
  // week on screen. Week-scoped detection used to miss an occurrence sitting in
  // another week and silently duplicate the recipe instead of offering to move.
  const allEntriesForRecipe = useSelector((state: RootState) =>
    recipeId ? selectPlannedMealsForRecipe(state, String(recipeId)) : EMPTY_MEALS
  );

  const movingMeal = useMemo(
    () => (movingMealId ? allEntriesForRecipe.find((m) => m.id === movingMealId) : undefined),
    [allEntriesForRecipe, movingMealId]
  );

  // The sheet can start in move mode and drop to add mode when the user asks
  // for a second helping, so the live mode is state rather than the prop.
  const [sheetMode, setSheetMode] = useState<AddToMealPlanMode>(mode);
  // "Plan a second time" is an explicit duplicate — don't then ask "already
  // scheduled elsewhere, move it?" on the very next tap.
  const [allowDuplicate, setAllowDuplicate] = useState(false);

  const isMoving = sheetMode === 'move' && !!movingMeal;

  // Everything sharing the slot the dish is leaving. A slot-mate means a swap
  // would drag it along, so the sheet stops offering one (see slotActionOptions).
  const sourceSlotMeals = useSelector((state: RootState) =>
    movingMeal
      ? selectMealsForDateRange(state, movingMeal.scheduledFor, movingMeal.scheduledFor)
      : EMPTY_MEALS
  );

  // Reset each time the modal opens: move mode lands on the week holding the
  // meal so "this meal is here" is on screen; add mode lands on this week.
  useEffect(() => {
    if (!visible) return;
    setSheetMode(mode);
    setAllowDuplicate(false);
    setModalWeekStart(
      mode === 'move' && movingMeal
        ? getWeekStartForDate(movingMeal.scheduledFor)
        : getCurrentMondayStart()
    );
  }, [visible, mode, movingMeal]);

  const pastStart = modalWeekStart;

  // Today, re-read at midnight and on foreground. The recipe page is a tab
  // that stays mounted for days: a date memoised at mount put « Aujourd'hui »
  // on Thursday for a cook planning on Friday, and every row was a day off (#845).
  const today = useToday();

  const weekEnd = useMemo(() => addDays(modalWeekStart, 6), [modalWeekStart]);

  const visibleMeals = useSelector(
    (state: RootState) => selectMealsForDateRange(state, pastStart, weekEnd)
  );

  // Upcoming entries of this recipe anywhere in the plan (for the move/dedupe
  // prompt). Past occurrences are history, not something to offer to move.
  const existingEntries = useMemo<PlannedMeal[]>(
    () => upcomingEntriesForRecipe(allEntriesForRecipe, today),
    [allEntriesForRecipe, today]
  );

  // Pending tap awaiting confirmation decision
  type PendingKind = 'sameRecipeElsewhere' | 'slotOccupied' | 'pastDay';
  const [pendingTap, setPendingTap] = useState<{
    date: string;
    mealType: MealType;
    kind: PendingKind;
    firstMealId?: string;
    /** Move mode: what this slot can do, so the user picks rather than the sheet. */
    options?: SlotActionOption[];
    /** `pastDay`: the slot's meals, to carry on with the tap once the cook says yes. */
    slotMeals?: PlannedMeal[];
    /** The cook already said they cooked it on that (past) day. */
    cooked?: boolean;
    /** Every dish a replace would take out of the slot, named (#845). */
    occupantLabels?: string[];
  } | null>(null);

  // Track day row positions for auto-scroll
  const handleDayLayout = useCallback((date: string, event: LayoutChangeEvent) => {
    dayYPositions.current[date] = event.nativeEvent.layout.y;
  }, []);

  // Auto-scroll to the meal being moved, else the preselected day, else today.
  // The anchor only applies to the week that actually contains it: once the
  // user browses to another week, holding the old offset left the top of the
  // new week scrolled off the screen, so its first days looked missing.
  const handleScrollContentLayout = useCallback(() => {
    if (!scrollViewRef.current) return;
    const inView = (date?: string) =>
      !!date && date >= modalWeekStart && date <= weekEnd;

    const anchorDate = (isMoving && inView(movingMeal?.scheduledFor))
      ? movingMeal!.scheduledFor
      : (inView(preselectedDate) ? preselectedDate : undefined);
    if (anchorDate) {
      const y = dayYPositions.current[anchorDate];
      if (y !== undefined && y > 0) {
        scrollViewRef.current.scrollTo({ y, animated: true });
        return;
      }
    }
    // Default: scroll to today so past days are hidden above the fold. In a
    // week that has no "today", that is the top of the list.
    const todayY = dayYPositions.current[today];
    scrollViewRef.current.scrollTo({ y: inView(today) && todayY !== undefined ? todayY : 0, animated: false });
  }, [preselectedDate, today, isMoving, movingMeal?.scheduledFor, modalWeekStart, weekEnd]);

  // Browsing weeks re-anchors the list; without this the offset from the
  // previous week survives and the new week opens part-scrolled.
  useEffect(() => {
    if (!visible) return;
    handleScrollContentLayout();
  }, [modalWeekStart, visible, handleScrollContentLayout]);

  // Build day data: 7 past days + today + rest of current week
  const weekData = useMemo((): DaySlots[] => {
    const result: DaySlots[] = [];
    const cursor = new Date(pastStart + 'T12:00:00');
    const end = new Date(weekEnd + 'T12:00:00');
    const availableMealsToday = getAvailableMealsForToday();

    while (cursor <= end) {
      const dateStr = toIsoDate(cursor);
      const isPast = dateStr < today;
      const isToday = dateStr === today;

      const mealsToShow = isToday ? availableMealsToday : MEAL_TYPES;

      // Skip today entirely if no meals are available
      if (isToday && mealsToShow.length === 0) {
        cursor.setDate(cursor.getDate() + 1);
        continue;
      }

      // day-of-week index: Mon=0 … Sun=6
      const dowIndex = ((cursor.getDay() + 6) % 7);

      const daySlots: DaySlots = {
        date: dateStr,
        dayName: t(DAY_NAME_KEYS[dowIndex]),
        dayIndex: dowIndex,
        isToday,
        isPast,
        slots: mealsToShow.map((mealType) => ({
          mealType,
          meals: visibleMeals.filter(
            (m) => m.scheduledFor === dateStr && m.mealType === mealType
          ),
        })),
      };

      result.push(daySlots);
      cursor.setDate(cursor.getDate() + 1);
    }

    return result;
  }, [pastStart, weekEnd, visibleMeals, today, t]);

  const pastDays = useMemo(() => weekData.filter(d => d.isPast), [weekData]);

  // Format date for display (e.g., "Dec 9")
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' });
  };

  const weekLabel = useMemo(
    () => `${formatDate(modalWeekStart)} – ${formatDate(weekEnd)}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalWeekStart, weekEnd, i18n.language]
  );

  const handlePrevWeek = useCallback(() => {
    setModalWeekStart(prev => {
      return addDays(prev, -7);
    });
  }, []);

  const handleNextWeek = useCallback(() => {
    setModalWeekStart(prev => {
      return addDays(prev, 7);
    });
  }, []);

  const handleSlotPress = (
    date: string,
    mealType: MealType,
    slotMeals: PlannedMeal[],
    cooked = false,
  ) => {
    // A day that is over: the cook says whether they cooked it. The app never
    // assumes it; a tap one day too high used to put a cook on the recipe that
    // never happened (#846).
    if (!cooked && asksIfCooked({ isMoving, date, today })) {
      setPendingTap({ date, mealType, kind: 'pastDay', slotMeals });
      return;
    }
    const withCook = cooked ? { cooked: true } : undefined;

    const outcome = resolveSlotTap({
      mode: sheetMode,
      movingMeal: isMoving ? movingMeal : undefined,
      sourceSlotMeals: isMoving
        ? sourceSlotMeals.filter((m) => m.mealType === movingMeal!.mealType)
        : undefined,
      allowDuplicate,
      recipeId,
      date,
      mealType,
      slotMeals,
      upcomingEntries: existingEntries,
    });

    switch (outcome.kind) {
      case 'noop':
        return;
      case 'move':
        onMove?.(outcome.mealId, date, mealType, 'move');
        onClose();
        return;
      case 'confirmSlotOccupied':
        setPendingTap({
          date,
          mealType,
          kind: 'slotOccupied',
          firstMealId: outcome.firstMealId,
          options: outcome.options,
          cooked,
          occupantLabels: slotMeals
            .filter((m) => !(isMoving && movingMeal && m.id === movingMeal.id))
            .map(slotMealLabel),
        });
        return;
      case 'confirmSameRecipeElsewhere':
        setPendingTap({ date, mealType, kind: 'sameRecipeElsewhere', cooked });
        return;
      case 'add':
        onAdd(date, mealType, 'add', undefined, withCook);
        onClose();
    }
  };

  // "Plan a second time" — drop out of move mode and let the user pick a slot
  // for a fresh occurrence, without the duplicate prompt second-guessing them.
  const handlePlanSecondTime = () => {
    setSheetMode('add');
    setAllowDuplicate(true);
  };

  const handleRemoveFromPlan = () => {
    if (!movingMeal) return;
    onRemove?.(movingMeal.id);
    onClose();
  };

  /** « Oui, je l'ai cuisinée »: carry on with the tap, now that the cook said so. */
  const handleConfirmCookedOnPastDay = () => {
    if (!pendingTap) return;
    const { date, mealType, slotMeals = [] } = pendingTap;
    setPendingTap(null);
    handleSlotPress(date, mealType, slotMeals, true);
  };

  const handleConfirmMove = () => {
    if (!pendingTap || existingEntries.length === 0) return;
    const fromMealId = existingEntries[0].id;
    onAdd(
      pendingTap.date,
      pendingTap.mealType,
      'move',
      fromMealId,
      pendingTap.cooked ? { cooked: true } : undefined,
    );
    setPendingTap(null);
    onClose();
  };

  /**
   * The user picked what an occupied slot should do. Move mode hands the choice
   * to the host as-is; add mode keeps its original replace/stack vocabulary.
   */
  const handleChooseSlotAction = (action: SlotAction) => {
    if (!pendingTap) return;
    const { date, mealType, firstMealId } = pendingTap;
    const withCook = pendingTap.cooked ? { cooked: true } : undefined;

    if (isMoving && movingMeal) {
      onMove?.(movingMeal.id, date, mealType, action as MoveAction);
    } else if (action === 'replace' && firstMealId) {
      onAdd(date, mealType, 'replace', firstMealId, withCook);
    } else {
      onAdd(date, mealType, 'add', undefined, withCook);
    }
    setPendingTap(null);
    onClose();
  };

  /**
   * What to call whatever is sitting in a slot. A slot can hold a resto, some
   * restes, a note or a "rien ce jour-là" as well as a recipe — those carry no
   * `recipe.title`, and rendering them as "Recette inconnue" made the plan look
   * corrupted from inside the sheet while the planner grid named them fine.
   */
  const slotMealLabel = useCallback((meal: PlannedMeal): string =>
    // "Restes" names the dish it came from, so resolve that id from the meals
    // already on screen.
    slotDishLabel(meal, t, (rid) =>
      visibleMeals.find((m) => String(m.recipeId) === String(rid))?.recipe?.title),
  [t, visibleMeals]);

  // « mercredi 21 octobre »: a past day can sit in another week than this one.
  const pastDayLabel = useMemo(() => {
    if (pendingTap?.kind !== 'pastDay') return '';
    return parseIsoDate(pendingTap.date).toLocaleDateString(
      i18n.language === 'fr' ? 'fr-FR' : 'en-US',
      { weekday: 'long', day: 'numeric', month: 'long' },
    );
  }, [pendingTap, i18n.language]);

  const existingEntryLabel = useMemo(() => {
    if (existingEntries.length === 0) return '';
    const first = existingEntries[0];
    const day = new Date(first.scheduledFor + 'T12:00:00').toLocaleDateString(
      i18n.language === 'fr' ? 'fr-FR' : 'en-US',
      { weekday: 'long' },
    );
    const meal = t('thisWeek.mealTypes.' + first.mealType);
    return `${day} · ${meal}`;
  }, [existingEntries, i18n.language, t]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable style={[styles.sheet, { paddingBottom: insets.bottom + PaprikaSpacing.md }]}>
          {/* Handle */}
          <View style={styles.handle} />

          {/* Header */}
          <View style={[styles.header, styles.headerBlock]}>
            <Text style={styles.title}>
              {isMoving
                ? t('recipeDetail.moveMealTitle', 'Déplacer ce repas')
                : t('recipeDetail.addToMealPlan')}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Feather name="x" size={24} color={Paprika.textPrimary} />
            </TouchableOpacity>
          </View>

          {/* Recipe being added */}
          <Text style={[styles.recipeName, styles.headerBlock]} numberOfLines={1}>
            {recipeTitle}
          </Text>

          {/* Week navigation */}
          <View style={[styles.weekNav, styles.headerBlock]}>
            <TouchableOpacity onPress={handlePrevWeek} style={styles.weekNavBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Feather name="chevron-left" size={20} color={Paprika.textPrimary} />
            </TouchableOpacity>
            <Text style={styles.weekNavLabel}>{weekLabel}</Text>
            <TouchableOpacity onPress={handleNextWeek} style={styles.weekNavBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Feather name="chevron-right" size={20} color={Paprika.textPrimary} />
            </TouchableOpacity>
          </View>

          {/* Week Grid */}
          <ScrollView
            ref={scrollViewRef}
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
            onContentSizeChange={handleScrollContentLayout}
          >
            {/* Scroll hint — visible when user scrolls up past today */}
            <Text style={styles.scrollHint}>{t('recipeDetail.pastMealsScrollHint')}</Text>

            {weekData.map((day) => {
              const isPreselectedDay = preselectedDate === day.date;
              const pastIdx = pastDays.indexOf(day);
              const rowOpacity = day.isPast
                ? 0.45 + 0.30 * (pastIdx / Math.max(pastDays.length - 1, 1))
                : 1;
              return (
                <React.Fragment key={day.date}>
                  {day.isToday && (
                    <View style={styles.todayDivider}>
                      <View style={styles.todayDividerLine} />
                      <Text style={styles.todayDividerLabel}>{t('thisWeek.today')}</Text>
                      <View style={styles.todayDividerLine} />
                    </View>
                  )}
                  <View
                    style={[styles.dayRow, { opacity: rowOpacity }]}
                    onLayout={(e) => handleDayLayout(day.date, e)}
                  >
                    {/* Day Label */}
                    <View style={[
                      styles.dayLabel,
                      day.isToday && styles.dayLabelToday,
                    ]}>
                      <Text style={[
                        styles.dayName,
                        day.isToday && styles.dayNameToday,
                        isPreselectedDay && styles.dayNamePreselected,
                      ]}>
                        {t(DAY_SHORT_KEYS[day.dayIndex])}
                      </Text>
                      <Text style={[
                        styles.dayDate,
                        day.isToday && styles.dayDateToday,
                        isPreselectedDay && styles.dayDatePreselected,
                      ]}>
                        {formatDate(day.date)}
                      </Text>
                    </View>

                    {/* Meal Slots */}
                    <View style={styles.slotsContainer}>
                      {day.slots.map((slot) => {
                        const isPreselectedSlot = isPreselectedDay && preselectedMealType === slot.mealType;
                        const isFull = slot.meals.length >= MAX_MEALS_PER_SLOT;
                        // The slot the moved meal currently sits in — marked,
                        // never a destination.
                        const isCurrentSlot = !!(
                          isMoving && movingMeal && slot.meals.some((m) => m.id === movingMeal.id)
                        );
                        const alreadyHere = !isCurrentSlot && recipeId
                          ? slot.meals.some((m) => String(m.recipeId) === String(recipeId))
                          : false;
                        // A full slot is still a destination when moving: it
                        // can be replaced or traded with. Only stacking is out,
                        // and the choice sheet says so. Add mode keeps the old
                        // "full means untappable" rule.
                        const blocked = isCurrentSlot || alreadyHere || (isFull && !isMoving);
                        const rowCopy = slotRowCopy({ isMoving, isPast: day.isPast, blocked });
                        const moveHere = rowCopy.action === 'moveHere' && (
                          <Text style={styles.slotMoveHere} testID="slot-move-here">
                            {t('recipeDetail.moveHere')}
                          </Text>
                        );
                        return (
                          <TouchableOpacity
                            key={slot.mealType}
                            style={[
                              styles.slot,
                              slot.meals.length > 0 && styles.slotOccupied,
                              isPreselectedSlot && styles.slotPreselected,
                              alreadyHere && styles.slotAlreadyHere,
                              isCurrentSlot && styles.slotCurrent,
                              isFull && !isCurrentSlot && styles.slotFull,
                            ]}
                            onPress={() => !blocked && handleSlotPress(day.date, slot.mealType, slot.meals)}
                            activeOpacity={blocked ? 1 : 0.7}
                            disabled={blocked}
                            testID={`plan-slot-${day.date}-${slot.mealType}`}
                          >
                            {slot.meals.length > 0 ? (
                              <>
                                <View style={styles.slotMealsList}>
                                  {isCurrentSlot ? (
                                    <View style={styles.slotCurrentRow}>
                                      <Ionicons name="restaurant-outline" size={13} color={Paprika.accent} />
                                      <Text style={styles.slotCurrentText} numberOfLines={1}>
                                        {t('recipeDetail.mealIsHere', 'Ce repas est ici')}
                                      </Text>
                                    </View>
                                  ) : (
                                    slot.meals.map((meal, idx) => (
                                      <Text key={meal.id} style={[styles.slotMealTitle, isFull && styles.slotMealTitleFull]} numberOfLines={1}>
                                        {idx > 0 ? '+ ' : ''}{slotMealLabel(meal)}
                                      </Text>
                                    ))
                                  )}
                                  {alreadyHere && (
                                    <Text style={styles.slotAlreadyHereBadge}>
                                      {t('recipeDetail.alreadyOnPlan')}
                                    </Text>
                                  )}
                                </View>
                                {moveHere}
                                {rowCopy.action === 'add' && (
                                  <Feather name="plus" size={12} color={Paprika.accent} />
                                )}
                              </>
                            ) : (
                              <>
                                {rowCopy.emptyLabel === 'logPast' ? (
                                  <Text style={styles.slotLogPast}>
                                    {t('recipeDetail.logPastMeal')}
                                  </Text>
                                ) : (
                                  <Text style={[
                                    styles.slotMealType,
                                    isPreselectedSlot && styles.slotMealTypePreselected,
                                  ]}>
                                    {t('thisWeek.mealTypes.' + slot.mealType)}
                                  </Text>
                                )}
                                {moveHere}
                                {rowCopy.action === 'add' && (
                                  <Feather
                                    name="plus"
                                    size={14}
                                    color={day.isPast ? Paprika.accent : (isPreselectedSlot ? Paprika.accent : Paprika.textSecondary)}
                                  />
                                )}
                              </>
                            )}
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>
                </React.Fragment>
              );
            })}
          </ScrollView>

          {/* Move mode carries the two other things you might want to do with a
              planned meal, so the sheet answers the whole question. */}
          {isMoving && (
            <View style={[styles.moveFooter, styles.headerBlock]}>
              <TouchableOpacity
                style={styles.moveFooterAction}
                onPress={handlePlanSecondTime}
                activeOpacity={0.7}
              >
                <Feather name="plus" size={15} color={Paprika.accent} />
                <Text style={styles.moveFooterActionText}>
                  {t('recipeDetail.planSecondTime', 'Planifier une 2ᵉ fois')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.moveFooterAction}
                onPress={handleRemoveFromPlan}
                activeOpacity={0.7}
              >
                <Text style={styles.moveFooterRemoveText}>
                  {t('recipeDetail.removeFromPlan', 'Retirer du plan')}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {pendingTap && (
            <Pressable
              style={styles.confirmOverlay}
              onPress={() => setPendingTap(null)}
            >
              <Pressable style={[styles.confirmSheet, { paddingBottom: insets.bottom + PaprikaSpacing.md }]}>
                {pendingTap.kind === 'pastDay' ? (
                  <>
                    <Text style={styles.confirmTitle}>
                      {t('recipeDetail.pastDayCookedTitle', { day: pastDayLabel })}
                    </Text>
                    <Text style={styles.confirmSubtitle}>
                      {t('recipeDetail.pastDayCookedSubtitle')}
                    </Text>
                    <TouchableOpacity
                      style={[styles.confirmBtn, styles.confirmBtnPrimary]}
                      onPress={handleConfirmCookedOnPastDay}
                      activeOpacity={0.85}
                      accessibilityRole="button"
                      testID="past-day-cooked-yes"
                    >
                      <Text style={styles.confirmBtnPrimaryText}>
                        {t('recipeDetail.pastDayCookedYes')}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.confirmBtn, styles.confirmBtnSecondary]}
                      onPress={() => setPendingTap(null)}
                      activeOpacity={0.85}
                      accessibilityRole="button"
                      testID="past-day-cooked-no"
                    >
                      <Text style={styles.confirmBtnSecondaryText}>
                        {t('recipeDetail.pastDayCookedNo')}
                      </Text>
                    </TouchableOpacity>
                  </>
                ) : pendingTap.kind === 'slotOccupied' ? (
                  <>
                    <Text style={styles.confirmTitle}>
                      {t('recipeDetail.slotOccupiedTitle')}
                    </Text>
                    <Text style={styles.confirmSubtitle} testID="slot-occupied-dishes">
                      {t('recipeDetail.slotAlreadyHolds', {
                        dishes: formatDishList(pendingTap.occupantLabels ?? [], i18n.language),
                      })}
                    </Text>
                    {(pendingTap.options ?? []).map((option, idx) => {
                      const label = t(SLOT_ACTION_LABEL_KEYS[option.action], {
                        count: pendingTap.occupantLabels?.length ?? 1,
                      });
                      if (!option.enabled) {
                        return (
                          <View
                            key={option.action}
                            style={[styles.confirmBtn, styles.confirmBtnDisabled]}
                            accessibilityRole="button"
                            accessibilityState={{ disabled: true }}
                            testID={`slot-action-${option.action}-disabled`}
                          >
                            <Text style={styles.confirmBtnDisabledText}>{label}</Text>
                            <Text style={styles.confirmBtnReason}>
                              {t(SLOT_BLOCKER_KEYS[option.blockedBy!])}
                            </Text>
                          </View>
                        );
                      }
                      // The first available choice reads as the default, the
                      // rest as alternatives — same shape the sheet always had.
                      const primary = idx === 0;
                      return (
                        <TouchableOpacity
                          key={option.action}
                          style={[styles.confirmBtn, primary ? styles.confirmBtnPrimary : styles.confirmBtnSecondary]}
                          onPress={() => handleChooseSlotAction(option.action)}
                          activeOpacity={0.85}
                          accessibilityRole="button"
                          testID={`slot-action-${option.action}`}
                        >
                          <Text style={primary ? styles.confirmBtnPrimaryText : styles.confirmBtnSecondaryText}>
                            {label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <Text style={styles.confirmTitle}>
                      {t('recipeDetail.alreadyScheduledTitle', { where: existingEntryLabel })}
                    </Text>
                    <Text style={styles.confirmSubtitle}>
                      {t('recipeDetail.alreadyScheduledSubtitle')}
                    </Text>
                    <TouchableOpacity
                      style={[styles.confirmBtn, styles.confirmBtnPrimary]}
                      onPress={handleConfirmMove}
                      activeOpacity={0.85}
                    >
                      <Text style={styles.confirmBtnPrimaryText}>
                        {t('recipeDetail.moveHere')}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.confirmBtn, styles.confirmBtnSecondary]}
                      onPress={() => handleChooseSlotAction('stack')}
                      activeOpacity={0.85}
                    >
                      <Text style={styles.confirmBtnSecondaryText}>
                        {t('recipeDetail.addAnother')}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
                {/* « Non, choisir un autre jour » already is the way out. */}
                {pendingTap.kind !== 'pastDay' && (
                  <TouchableOpacity
                    style={styles.confirmCancel}
                    onPress={() => setPendingTap(null)}
                    activeOpacity={0.6}
                  >
                    <Text style={styles.confirmCancelText}>{t('common.cancel')}</Text>
                  </TouchableOpacity>
                )}
              </Pressable>
            </Pressable>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Paprika.background,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    maxHeight: '80%',
    ...Platform.select({
      ios: {
        shadowColor: '#52443c',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      },
      android: {
        elevation: 16,
      },
      web: {
        width: '100%',
        maxWidth: 560,
        alignSelf: 'center',
      },
    }),
  },
  handle: {
    width: 40,
    height: 4,
    backgroundColor: Paprika.border,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.sm,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
  },
  closeButton: {
    padding: PaprikaSpacing.xs,
  },
  recipeName: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textSecondary,
    paddingHorizontal: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.sm,
  },
  weekNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.md,
  },
  weekNavBtn: {
    padding: PaprikaSpacing.xs,
  },
  weekNavLabel: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textPrimary,
  },
  // The sheet is capped at 80% of the screen, so something has to give when the
  // week grid is taller than that. Without this it was the header block that
  // collapsed — the recipe title got clipped behind the week nav.
  scrollView: {
    paddingHorizontal: PaprikaSpacing.lg,
    flexShrink: 1,
  },
  headerBlock: {
    flexShrink: 0,
  },
  dayRow: {
    flexDirection: 'row',
    marginBottom: PaprikaSpacing.md,
    alignItems: 'flex-start',
  },
  dayLabel: {
    width: 50,
    paddingTop: PaprikaSpacing.xs,
  },
  dayLabelToday: {},
  dayName: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
  dayNameToday: {
    color: Paprika.accent,
  },
  dayDate: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
  },
  dayDateToday: {
    color: Paprika.accent,
  },
  slotsContainer: {
    flex: 1,
    gap: PaprikaSpacing.xs,
  },
  slot: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    backgroundColor: Paprika.surfaceLight,
    borderRadius: PaprikaRadius.md,
    minHeight: 40,
  },
  slotOccupied: {
    backgroundColor: Paprika.cardBg,
    borderWidth: 1,
    borderColor: Paprika.borderLight,
  },
  slotMealType: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  // Keeps its width: a long dish title is what gets cut, never the action.
  slotMoveHere: {
    ...PaprikaTypography.bodySmall,
    fontFamily: 'Inter-SemiBold',
    color: Paprika.accent,
    flexShrink: 0,
  },
  slotPreselected: {
    borderWidth: 2,
    borderColor: Paprika.accent,
    backgroundColor: `${Paprika.accent}10`,
  },
  slotMealTypePreselected: {
    color: Paprika.accent,
    fontWeight: '600',
  },
  dayNamePreselected: {
    color: Paprika.accent,
    fontWeight: '700',
  },
  dayDatePreselected: {
    color: Paprika.accent,
  },
  slotMealsList: {
    flex: 1,
    marginRight: PaprikaSpacing.sm,
  },
  slotMealTitle: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textPrimary,
  },
  slotMealTitleFull: {
    color: Paprika.textMuted,
  },
  slotFull: {
    opacity: 0.5,
  },
  slotAlreadyHere: {
    backgroundColor: '#FFF3CD',
    borderWidth: 1.5,
    borderColor: '#F0AD4E',
  },
  // The meal being moved: peach paper with a terracotta rail on the leading
  // edge — reads as "you are here", not as a target.
  slotCurrent: {
    backgroundColor: Paprika.accentLight,
    borderWidth: 0,
    borderLeftWidth: 3,
    borderLeftColor: Paprika.accent,
  },
  slotCurrentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.xs,
  },
  slotCurrentText: {
    ...PaprikaTypography.bodySmall,
    fontFamily: 'Inter-SemiBold',
    color: Paprika.accent,
  },
  moveFooter: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: PaprikaSpacing.sm,
    backgroundColor: Paprika.surfaceLow,
  },
  moveFooterAction: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: PaprikaSpacing.xs,
    paddingVertical: PaprikaSpacing.md,
  },
  moveFooterActionText: {
    ...PaprikaTypography.bodyMedium,
    fontFamily: 'Inter-SemiBold',
    color: Paprika.accent,
  },
  moveFooterRemoveText: {
    ...PaprikaTypography.bodyMedium,
    fontFamily: 'Inter-SemiBold',
    color: Paprika.destructive,
  },
  slotAlreadyHereBadge: {
    ...PaprikaTypography.caption,
    color: '#B8860B',
    marginTop: 2,
  },
  confirmOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'flex-end',
  },
  confirmSheet: {
    backgroundColor: Paprika.background,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: PaprikaSpacing.lg,
  },
  confirmTitle: {
    ...PaprikaTypography.bodyLarge,
    fontWeight: '700',
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
  },
  confirmSubtitle: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.lg,
  },
  confirmBtn: {
    paddingVertical: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.md,
    alignItems: 'center',
    marginBottom: PaprikaSpacing.sm,
  },
  confirmBtnPrimary: {
    backgroundColor: Paprika.accent,
  },
  confirmBtnPrimaryText: {
    ...PaprikaTypography.bodyMedium,
    color: '#FFF',
    fontWeight: '600',
  },
  confirmBtnSecondary: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderColor: Paprika.accent,
  },
  confirmBtnSecondaryText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.accent,
    fontWeight: '600',
  },
  // An unavailable choice stays on screen, greyed with its reason: a row that
  // silently does nothing when tapped reads as a broken app, not a full slot.
  confirmBtnDisabled: {
    backgroundColor: Paprika.surfaceLow,
    alignItems: 'center',
  },
  confirmBtnDisabledText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textMuted,
    fontWeight: '600',
  },
  confirmBtnReason: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginTop: 2,
  },
  confirmCancel: {
    paddingVertical: PaprikaSpacing.sm,
    alignItems: 'center',
  },
  confirmCancelText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  scrollHint: {
    textAlign: 'center',
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    fontStyle: 'italic',
    marginBottom: PaprikaSpacing.sm,
  },
  todayDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.md,
  },
  todayDividerLine: {
    flex: 1,
    borderTopWidth: 1.5,
    borderTopColor: Paprika.border,
    borderStyle: 'dashed',
  },
  todayDividerLabel: {
    ...PaprikaTypography.caption,
    color: Paprika.accent,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  slotLogPast: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textMuted,
    fontStyle: 'italic',
  },
});

export default AddToMealPlanModal;
